/** Enemy — random-walk + player-track + trail-target + sleeper + patroller + guard AI.
 *  Reference: docs/STAGE_DESIGN.md §3.2 type_T3_survival.enemy_ai_difficulty
 *  - random_walk: 8-directional random walk (easy)
 *  - player_track: greedy chaser that picks direction reducing Chebyshev distance to player
 *  - trail_target: prefers trail cells (Stalker preview)
 *  - sleeper: 静止→プレイヤーが wake_radius 以内で起動→以後 player_track
 *  - patroller: patrol_path の waypoint を順に巡回
 *  - guard: guard_anchor 中心の半径 2 矩形周回(時計回り)
 *  端で必ず方向転換 / 接触判定は外部(GameLoop)で行う。
 */

import { Container, Graphics } from 'pixi.js';
import type { Direction, GridConfig, GridPos } from './types';
import {
  ALL_DIRECTIONS,
  DIRECTION_DELTA,
  directionStepDistance,
  inBounds,
  isReverse,
} from './types';

export type EnemyAi =
  | 'random_walk'
  | 'player_track'
  | 'trail_target'
  | 'sleeper'
  | 'patroller'
  | 'guard'
  | 'mirror_echo';

export interface EnemyOptions {
  readonly grid: GridConfig;
  readonly initialPos: GridPos;
  readonly color: number;
  readonly speedCellsPerSec: number;
  readonly directionChangeChance: number; // 0..1 per cell entry
  readonly ai?: EnemyAi;
  /** player_track / trail_target / sleeper が参照するプレイヤー位置取得関数。 */
  readonly getPlayerPos?: () => GridPos;
  /** trail_target が参照するトレイルセル一覧(セル単位)。 */
  readonly getTrailCells?: () => readonly GridPos[];
  /** sleeper: 起動半径(デフォルト 5) */
  readonly wakeRadius?: number;
  /** patroller: 巡回 waypoint リスト */
  readonly patrolPath?: readonly GridPos[];
  /** guard: 守備中心座標 */
  readonly guardAnchor?: GridPos;
  /** mirror_echo: プレイヤーの過去位置履歴(300 frames リングバッファ)取得関数。
   *  GameplayScreen が管理し、5 秒前の位置を読んで enemy を teleport させる。 */
  readonly getPlayerHistory?: () => readonly GridPos[];
}

export class Enemy {
  public readonly view: Container;

  private readonly grid: GridConfig;
  private readonly speedCellsPerSec: number;
  private readonly directionChangeChance: number;
  private readonly graphics: Graphics;
  private readonly ai: EnemyAi;
  private readonly getPlayerPos?: () => GridPos;
  private readonly getTrailCells?: () => readonly GridPos[];
  private readonly getPlayerHistory?: () => readonly GridPos[];

  // sleeper
  private readonly wakeRadius: number;
  private sleeperAwake = false;

  // patroller
  private readonly patrolPath: readonly GridPos[];
  private patrolIndex = 0;

  // guard
  private readonly guardAnchor: GridPos | null;
  private guardRing: GridPos[] = [];
  private guardRingIndex = 0;

  private gridPos: GridPos;
  private direction: Direction;
  private cellProgress = 0;

  constructor(options: EnemyOptions) {
    this.grid = options.grid;
    this.speedCellsPerSec = options.speedCellsPerSec;
    this.directionChangeChance = options.directionChangeChance;
    this.ai = options.ai ?? 'random_walk';
    this.getPlayerPos = options.getPlayerPos;
    this.getTrailCells = options.getTrailCells;
    this.getPlayerHistory = options.getPlayerHistory;
    this.wakeRadius = options.wakeRadius ?? 5;
    this.patrolPath = options.patrolPath ?? [];
    this.guardAnchor = options.guardAnchor ?? null;
    this.gridPos = options.initialPos;

    // Guard: anchor 周辺半径 2 の矩形周回リストを事前計算(時計回り)
    if (this.ai === 'guard' && this.guardAnchor) {
      this.guardRing = buildGuardRing(this.guardAnchor, 2, this.grid.gridSize);
      this.guardRingIndex = 0;
    }

    this.direction = this.pickDirection(null);

    this.view = new Container();
    this.graphics = new Graphics();
    this.drawShape(options.color);
    this.view.addChild(this.graphics);

    this.updateRenderPos();
  }

  update(deltaSeconds: number): GridPos | null {
    // mirror_echo: リングバッファから 5 秒前の位置を読んで瞬間移動
    if (this.ai === 'mirror_echo') {
      if (this.getPlayerHistory) {
        const history = this.getPlayerHistory();
        // 5 秒前 = 約 300 フレーム前(60 fps × 5 秒)。履歴が短い場合は先頭を使う。
        const targetIdx = Math.max(0, history.length - 300 - 1);
        const target = history[targetIdx];
        if (target) {
          this.gridPos = { x: target.x, y: target.y };
        }
      }
      this.updateRenderPos();
      return this.gridPos;
    }

    // sleeper: 起動判定
    if (this.ai === 'sleeper' && !this.sleeperAwake && this.getPlayerPos) {
      const ppos = this.getPlayerPos();
      if (chebyshev(this.gridPos, ppos) <= this.wakeRadius) {
        this.sleeperAwake = true;
        // 起動時に赤目描画に差し替え
        this.graphics.clear();
        this.drawSleeperAwake();
      }
    }

    // sleeper かつ未起動 → 静止(位置更新なし)
    if (this.ai === 'sleeper' && !this.sleeperAwake) {
      return null;
    }

    const stepDistance = directionStepDistance(this.direction);
    this.cellProgress += (deltaSeconds * this.speedCellsPerSec) / stepDistance;

    let entered: GridPos | null = null;
    while (this.cellProgress >= 1.0) {
      this.cellProgress -= 1.0;

      const wouldStep = this.tryStep(this.direction);
      const randomChange = Math.random() < this.directionChangeChance;
      // player_track / trail_target / sleeper(起動後) / patroller / guard は毎セル見直し
      const aiForceChange =
        this.ai !== 'random_walk' &&
        !(this.ai === 'sleeper' && !this.sleeperAwake);
      const shouldChange = wouldStep === null || randomChange || aiForceChange;
      if (shouldChange) {
        this.direction = this.pickDirection(this.direction);
      }

      const stepResult = this.tryStep(this.direction);
      if (stepResult) {
        this.gridPos = stepResult;
        entered = stepResult;
        // patroller: waypoint 到達チェック
        if (this.ai === 'patroller' && this.patrolPath.length > 0) {
          const target = this.patrolPath[this.patrolIndex]!;
          if (this.gridPos.x === target.x && this.gridPos.y === target.y) {
            this.patrolIndex = (this.patrolIndex + 1) % this.patrolPath.length;
          }
        }
        // guard: ring セル到達チェック
        if (this.ai === 'guard' && this.guardRing.length > 0) {
          const target = this.guardRing[this.guardRingIndex]!;
          if (this.gridPos.x === target.x && this.gridPos.y === target.y) {
            this.guardRingIndex = (this.guardRingIndex + 1) % this.guardRing.length;
          }
        }
      } else {
        this.cellProgress = 0;
        break;
      }
    }

    this.updateRenderPos();
    return entered;
  }

  getGridPos(): GridPos {
    return this.gridPos;
  }

  private tryStep(dir: Direction): GridPos | null {
    const delta = DIRECTION_DELTA[dir];
    const newPos: GridPos = {
      x: this.gridPos.x + delta.x,
      y: this.gridPos.y + delta.y,
    };
    if (!inBounds(newPos, this.grid.gridSize)) return null;
    return newPos;
  }

  /** AI 種別に応じた方向選択ディスパッチャ。 */
  private pickDirection(avoid: Direction | null): Direction {
    switch (this.ai) {
      case 'player_track':
        return this.pickTrackingDirection(avoid);
      case 'trail_target':
        return this.pickTrailTargetDirection(avoid);
      case 'sleeper':
        // 起動後は player_track と同じロジック
        return this.sleeperAwake
          ? this.pickTrackingDirection(avoid)
          : this.pickRandomDirection(avoid);
      case 'patroller':
        return this.pickPatrollerDirection(avoid);
      case 'guard':
        return this.pickGuardDirection(avoid);
      case 'mirror_echo':
        // update() 側で位置を直接書き換えるため、direction は random で良い
        return this.pickRandomDirection(avoid);
      case 'random_walk':
      default:
        return this.pickRandomDirection(avoid);
    }
  }

  /** プレイヤー方向に Chebyshev 距離が縮む候補から選ぶ。同距離は乱択。
   *  20% で random_walk にフォールバックして「読めない動き」を残す。
   */
  private pickTrackingDirection(avoid: Direction | null): Direction {
    if (!this.getPlayerPos) return this.pickRandomDirection(avoid);
    if (Math.random() < 0.2) return this.pickRandomDirection(avoid);

    const target = this.getPlayerPos();
    return this.pickBestDirectionToward(target, avoid);
  }

  /** トレイルセル群のうち最も近いセルへ向かう。トレイルが空ならプレイヤー追跡にフォールバック。 */
  private pickTrailTargetDirection(avoid: Direction | null): Direction {
    const cells = this.getTrailCells?.();
    if (!cells || cells.length === 0) {
      return this.pickTrackingDirection(avoid);
    }
    let nearest: GridPos = cells[0]!;
    let nearestDist = chebyshev(this.gridPos, nearest);
    for (const cell of cells) {
      const d = chebyshev(this.gridPos, cell);
      if (d < nearestDist) {
        nearestDist = d;
        nearest = cell;
      }
    }
    return this.pickBestDirectionToward(nearest, avoid);
  }

  /** patroller: patrol_path[patrolIndex] に向かう最短方向。 */
  private pickPatrollerDirection(avoid: Direction | null): Direction {
    if (this.patrolPath.length === 0) return this.pickRandomDirection(avoid);
    const target = this.patrolPath[this.patrolIndex]!;
    // すでに同座標なら次 waypoint を先読み
    if (this.gridPos.x === target.x && this.gridPos.y === target.y) {
      const nextIdx = (this.patrolIndex + 1) % this.patrolPath.length;
      return this.pickBestDirectionToward(this.patrolPath[nextIdx]!, avoid);
    }
    return this.pickBestDirectionToward(target, avoid);
  }

  /** guard: guardRing[guardRingIndex] に向かう。 */
  private pickGuardDirection(avoid: Direction | null): Direction {
    if (this.guardRing.length === 0) return this.pickRandomDirection(avoid);
    const target = this.guardRing[this.guardRingIndex]!;
    if (this.gridPos.x === target.x && this.gridPos.y === target.y) {
      const nextIdx = (this.guardRingIndex + 1) % this.guardRing.length;
      return this.pickBestDirectionToward(this.guardRing[nextIdx]!, avoid);
    }
    return this.pickBestDirectionToward(target, avoid);
  }

  /** target に向かって Chebyshev 距離を縮める候補の中から乱択。
   *  進入不能(端)・逆走は除外。候補ゼロ時は random_walk フォールバック。
   */
  private pickBestDirectionToward(target: GridPos, avoid: Direction | null): Direction {
    const currentDist = chebyshev(this.gridPos, target);
    const reducing: Direction[] = [];
    const neutral: Direction[] = [];

    for (const d of ALL_DIRECTIONS) {
      if (avoid !== null && isReverse(avoid, d)) continue;
      const delta = DIRECTION_DELTA[d];
      const peek: GridPos = {
        x: this.gridPos.x + delta.x,
        y: this.gridPos.y + delta.y,
      };
      if (!inBounds(peek, this.grid.gridSize)) continue;
      const newDist = chebyshev(peek, target);
      if (newDist < currentDist) reducing.push(d);
      else if (newDist === currentDist) neutral.push(d);
    }

    if (reducing.length > 0) {
      return reducing[Math.floor(Math.random() * reducing.length)]!;
    }
    if (neutral.length > 0) {
      return neutral[Math.floor(Math.random() * neutral.length)]!;
    }
    return this.pickRandomDirection(avoid);
  }

  private pickRandomDirection(avoid: Direction | null): Direction {
    const candidates = ALL_DIRECTIONS.filter((d) => {
      if (avoid !== null && isReverse(avoid, d)) return false;
      const delta = DIRECTION_DELTA[d];
      const peek: GridPos = {
        x: this.gridPos.x + delta.x,
        y: this.gridPos.y + delta.y,
      };
      return inBounds(peek, this.grid.gridSize);
    });
    if (candidates.length === 0) {
      return ALL_DIRECTIONS[Math.floor(Math.random() * ALL_DIRECTIONS.length)]!;
    }
    return candidates[Math.floor(Math.random() * candidates.length)]!;
  }

  /** AI 種別に応じた外観(チェイサー=三角、トレイル狩り=ダイヤ、ランダム=丸、
   *  sleeper=灰丸+目アイコン、patroller=青正方形、guard=赤盾型、
   *  mirror_echo=半透明グレー紫の影シルエット)。 */
  private drawShape(color: number): void {
    const r = this.grid.cellPx * 0.4;
    if (this.ai === 'mirror_echo') {
      // 半透明グレー紫の影シルエット(プレイヤーの残像)
      this.graphics.circle(0, 0, r).fill({ color: 0x9988bb, alpha: 0.55 });
      this.graphics.circle(0, 0, r).stroke({ width: 2, color: 0x6655aa, alpha: 0.7 });
      // 中心に小さなグレー点
      this.graphics.circle(0, 0, r * 0.25).fill({ color: 0xddccff, alpha: 0.65 });
    } else if (this.ai === 'player_track') {
      this.graphics
        .moveTo(0, -r)
        .lineTo(r * 0.9, r * 0.7)
        .lineTo(-r * 0.9, r * 0.7)
        .closePath()
        .fill({ color });
      this.graphics.circle(0, 0, r * 0.18).fill({ color: 0xff5577 });
    } else if (this.ai === 'trail_target') {
      this.graphics
        .moveTo(0, -r)
        .lineTo(r, 0)
        .lineTo(0, r)
        .lineTo(-r, 0)
        .closePath()
        .fill({ color });
    } else if (this.ai === 'sleeper') {
      // 灰色丸 + 閉じた目(スリープ状態)
      this.graphics.circle(0, 0, r).fill({ color: 0x888899 });
      this.graphics
        .moveTo(-r * 0.4, 0)
        .lineTo(r * 0.4, 0)
        .stroke({ width: Math.max(1.5, r * 0.12), color: 0x444455, alpha: 0.8, cap: 'round' });
    } else if (this.ai === 'patroller') {
      // 青正方形
      const hs = r * 0.85;
      this.graphics.rect(-hs, -hs, hs * 2, hs * 2).fill({ color: 0x2277dd });
      this.graphics.circle(0, 0, r * 0.2).fill({ color: 0xaaddff });
    } else if (this.ai === 'guard') {
      // 赤盾型(五角形)
      this.graphics
        .moveTo(0, -r)
        .lineTo(r * 0.85, -r * 0.3)
        .lineTo(r * 0.6, r * 0.8)
        .lineTo(-r * 0.6, r * 0.8)
        .lineTo(-r * 0.85, -r * 0.3)
        .closePath()
        .fill({ color: 0xdd2222 });
      this.graphics.circle(0, r * 0.1, r * 0.22).fill({ color: 0xff8888, alpha: 0.7 });
    } else {
      this.graphics.circle(0, 0, this.grid.cellPx * 0.35).fill({ color });
    }
  }

  /** sleeper 起動時の赤目描画(Graphics を差し替え)。 */
  private drawSleeperAwake(): void {
    const r = this.grid.cellPx * 0.4;
    this.graphics.circle(0, 0, r).fill({ color: 0x993399 });
    // 赤い目
    this.graphics.circle(-r * 0.28, -r * 0.1, r * 0.18).fill({ color: 0xff2244 });
    this.graphics.circle(r * 0.28, -r * 0.1, r * 0.18).fill({ color: 0xff2244 });
    this.graphics.circle(-r * 0.24, -r * 0.08, r * 0.07).fill({ color: 0xffffff, alpha: 0.85 });
    this.graphics.circle(r * 0.32, -r * 0.08, r * 0.07).fill({ color: 0xffffff, alpha: 0.85 });
  }

  private updateRenderPos(): void {
    const delta = DIRECTION_DELTA[this.direction];
    const interpX = this.gridPos.x + delta.x * this.cellProgress;
    const interpY = this.gridPos.y + delta.y * this.cellProgress;
    this.view.x = (interpX + 0.5) * this.grid.cellPx;
    this.view.y = (interpY + 0.5) * this.grid.cellPx;
  }
}

function chebyshev(a: GridPos, b: GridPos): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/**
 * anchor 周辺の半径 r セル矩形境界を時計回りで列挙。
 * 上辺左→右、右辺上→下、下辺右→左、左辺下→上の順。
 * inBounds チェックつき。
 */
function buildGuardRing(anchor: GridPos, radius: number, gridSize: number): GridPos[] {
  const ring: GridPos[] = [];
  const x0 = anchor.x - radius;
  const y0 = anchor.y - radius;
  const x1 = anchor.x + radius;
  const y1 = anchor.y + radius;

  // 上辺: y=y0, x: x0→x1
  for (let x = x0; x <= x1; x++) {
    const p = { x, y: y0 };
    if (inBounds(p, gridSize)) ring.push(p);
  }
  // 右辺: x=x1, y: y0+1→y1
  for (let y = y0 + 1; y <= y1; y++) {
    const p = { x: x1, y };
    if (inBounds(p, gridSize)) ring.push(p);
  }
  // 下辺: y=y1, x: x1-1→x0
  for (let x = x1 - 1; x >= x0; x--) {
    const p = { x, y: y1 };
    if (inBounds(p, gridSize)) ring.push(p);
  }
  // 左辺: x=x0, y: y1-1→y0+1
  for (let y = y1 - 1; y > y0; y--) {
    const p = { x: x0, y };
    if (inBounds(p, gridSize)) ring.push(p);
  }

  return ring.length > 0 ? ring : [anchor];
}
